import React, { useEffect, useState } from "react"
import { LandingCTA } from "./LandingCTA"
import { LandingHeroBrackyIntro } from "./LandingHeroBrackyIntro"
import { LandingSectionHero } from "./LandingSectionHero"
import { introBasketballClip } from "./LandingSounds"

/**
 * LandingHeroLockup
 * Combines the main hero headline and the Bracky intro into a single
 * vertically-stacked lock-up so we can control their relative spacing
 * in one place.  The component is self-contained so it can be centered
 * via flexbox wherever it is used.
 */
export const LandingHeroLockup = React.memo(function LandingHeroLockup({ ctaLabel }: { ctaLabel?: string } = {}) {
  // Track when Bracky's glow is enabled
  const [_brackyGlowEnabled, setBrackyGlowEnabled] = useState(false)

  // Reference the intro clip to ensure it's preloaded (no-op usage)
  void introBasketballClip

  // Separate visibility states for microcopy and CTA fade-out timing
  const [microcopyVisible, setMicrocopyVisible] = useState(false)
  const [ctaVisible, setCtaVisible] = useState(false)

  // Listen for Bracky's glow enable event
  useEffect(() => {
    const handleBrackyGlow = () => setBrackyGlowEnabled(true)
    window.addEventListener("brackyInitialComplete", handleBrackyGlow)
    return () => window.removeEventListener("brackyInitialComplete", handleBrackyGlow)
  }, [])

  // Fade in both elements after mount with slight delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setMicrocopyVisible(true)
      setCtaVisible(true)
    }, 500)
    return () => clearTimeout(timer)
  }, [])

  // Handle separate scroll-based fade-out timing
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY

      // Microcopy fades out first at lower scroll threshold
      setMicrocopyVisible(scrollY <= 50)

      // CTA fades out later at higher scroll threshold
      setCtaVisible(scrollY <= 150)
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  // Scroll helper – mimic LandingNav scrollToWithOffset for the `#bracky` anchor - unused
  // const _handleScrollToBracky = () => {
  //   if (typeof window === "undefined") return

  //   const el = document.getElementById("bracky")
  //   if (!el) return

  //   // Maintain same extra spacing used in LandingNav so content clears the fixed nav/header
  //   const EXTRA_SPACING = -25
  //   const navEl = document.getElementById("landing-nav")
  //   const headerOffset = (navEl ? navEl.getBoundingClientRect().bottom : 90) + EXTRA_SPACING

  //   const elementY = el.getBoundingClientRect().top + window.scrollY
  //   const targetY = elementY - headerOffset

  //   window.scrollTo({ top: targetY, behavior: "smooth" })
  // }

  // Calculate extended background glow that covers subtitle area - unused
  // const _getBackgroundGlowStyle = () => {
  //   if (!brackyGlowEnabled) return {}

  //   const isMobile = typeof window !== "undefined" ? window.innerWidth < 640 : false
  //   const BLUR_BASE = isMobile ? 300 : 450 // Larger than avatar glow
  //   const SPREAD_BASE = isMobile ? 200 : 300 // Larger spread to cover subtitle
  //   const GLOW_STRENGTH = isMobile ? 0.8 : 0.6 // Slightly weaker for background
  //   const haloOpacity = 0.4 // More subtle for background

  //   const haloBlur = BLUR_BASE * GLOW_STRENGTH
  //   const haloSpread = SPREAD_BASE * GLOW_STRENGTH

  //   const backgroundGlow = `0 0 ${haloBlur}px ${haloSpread}px rgba(0,28,255,${haloOpacity})`

  //   return {
  //     boxShadow: backgroundGlow,
  //     transition: "box-shadow 0.75s ease-out",
  //   }
  // }

  return (
    <div className="flex flex-col items-center text-center">
      {/* Scaled lock-up (hero + intro) */}
      <div className="flex flex-col items-center scale-[1.05] md:scale-[1.1] tall:scale-[1.15]">
        {/* Hero */}
        <div className="shrink-0 scale-[.95] -mb-12 -mt-[1.8rem] sm:scale-[1.05] sm:-mb-[1.6rem] sm:mt-[4em] md:scale-[1.15] md:-mb-18 md:mt-4 lg:scale-[1.2] lg:-mb-20 lg:mt-4 xl:-mt-4 xl:-mb-[7.3rem] 2xl:-mb-42">
          <LandingSectionHero />
        </div>

        {/* Intro */}
        <div>
          <LandingHeroBrackyIntro />
        </div>
      </div>

      {/* Tall-screen CTA – outside scaling wrapper */}
      <div className="hidden tall:flex flex-col items-center z-50 justify-center sm:mt-6 md:mt-8 lg:mt-10 xl:mt-12">
        <LandingCTA variant="tall-screen" externalVisibility={ctaVisible} label={ctaLabel ?? undefined} />

        {/* Microcopy */}
        <p
          className="mt-4 text-xs sm:text-sm text-white/50 text-center transition-opacity duration-300 ease-out"
          style={{ opacity: microcopyVisible ? 1 : 0 }}
        >
          {/* Bracket.Game Funnel */}
          Sign in with X—Start betting live in seconds
        </p>
      </div>
    </div>
  )
})
